import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Check, Copy, Heart, Shield, Building2, ArrowRight, Quote } from 'lucide-react';

const DonationReceipt = () => {
  const { donationId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Every.org sends abandoned checkouts back with ?exited=1 — record it so we can tell
    // "donor backed out" apart from "donation completed but we were never notified"
    if (new URLSearchParams(window.location.search).get('exited') === '1') {
      api.post(`/api/everyorg/checkout-exited/${donationId}`).catch(() => {});
    }
    api.get(`/api/donations/track/${donationId}`)
      .then(r => setReceipt(r.data))
      .catch(() => setReceipt(null))
      .finally(() => setLoading(false));
  }, [donationId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Receipt link copied — share it with your team');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#07090E' }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: '#07090E' }}>
        <h1 className="text-2xl font-bold text-slate-100">Receipt not found</h1>
        <p className="text-sm text-slate-400 mt-3">We couldn't find a donation with this reference.</p>
        <Link to="/" className="mt-6 text-sm font-semibold text-blue-400 hover:text-blue-300">Back to Altrunic →</Link>
      </div>
    );
  }

  const total = (receipt.amount || 0) + (receipt.employer_match_amount || 0);
  const disbursed = receipt.disbursement_status === 'disbursed';
  const DISBURSEMENT_LABELS = {
    awaiting_donation: 'Awaiting payment',
    awaiting_payment: 'Awaiting payment',
    with_everyorg: 'Held by Every.org for granting',
    processing: 'Processing',
    disbursed: 'Received by the charity',
  };
  const disbursementLabel = DISBURSEMENT_LABELS[receipt.disbursement_status] || 'Processing';
  const viaEveryorg = receipt.rail === 'everyorg';

  return (
    <div className="min-h-screen py-16 px-6" style={{ background: '#07090E', fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }} data-testid="donation-receipt-page">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="flex items-center gap-2.5 mb-8">
          <img src="/logo-mark.png" alt="Altrunic" className="w-8 h-8 object-contain" />
          <span className="text-lg font-bold tracking-tight text-slate-50">Altrunic</span>
        </Link>

        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-[0_16px_48px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
              <Heart className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Donation receipt</p>
              <p className="text-sm text-slate-400 mt-0.5">{receipt.donation_id}</p>
            </div>
          </div>

          <p className="mt-8 text-5xl font-extrabold text-slate-50 tracking-tight" data-testid="receipt-total">${total.toLocaleString()}</p>
          <p className="text-sm text-slate-400 mt-2">delivered to <strong className="text-slate-100">{receipt.charity_name}</strong></p>

          <div className="mt-8 space-y-3 border-t border-slate-800 pt-6 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Your donation</span><span className="font-semibold text-slate-200">${(receipt.amount || 0).toLocaleString()}</span></div>
            {receipt.employer_match_amount > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Employer match</span><span className="font-semibold text-blue-400">+${receipt.employer_match_amount.toLocaleString()}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">Payment</span><span className="font-semibold text-slate-200 capitalize">{receipt.payment_status || 'pending'}</span></div>
            <div className="flex justify-between">
              <span className="text-slate-500">Charity disbursement</span>
              <span className={`font-semibold ${disbursed ? 'text-blue-400' : 'text-amber-400'}`} data-testid="receipt-disbursement-status">
                {disbursementLabel}
              </span>
            </div>
            {receipt.disbursement_reference && (
              <div className="flex justify-between">
                <span className="text-slate-500">Disbursement reference</span>
                <span className="font-semibold text-slate-200">{receipt.disbursement_reference}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">Date</span>
              <span className="font-semibold text-slate-200">{new Date(receipt.paid_at || receipt.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2.5 bg-slate-950/60 border border-slate-800 rounded-xl p-4">
            <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-400 leading-relaxed">
              {viaEveryorg
                ? 'Processed and receipted by Every.org, a 501(c)(3) public charity, which grants the funds to the recipient nonprofit. Altrunic charges no platform fee.'
                : 'Payment processed securely by Stripe. Altrunic charges no platform fee on donations.'}
            </p>
          </div>

          <button onClick={copyLink} className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-3 rounded-xl text-sm transition-colors" data-testid="receipt-share-btn">
            <Copy className="w-4 h-4" /> Copy shareable link
          </button>
        </div>

        {receipt.charity_note && (
          <div className="mt-6 bg-slate-900/70 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6" data-testid="receipt-charity-note">
            <div className="flex items-center gap-2 mb-3">
              <Quote className="w-4 h-4 text-blue-400" />
              <p className="text-xs font-semibold text-blue-400 tracking-[0.15em] uppercase font-mono">Note from the charity</p>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed italic">&ldquo;{receipt.charity_note.message}&rdquo;</p>
            <p className="mt-4 text-xs text-slate-500">
              — {receipt.charity_note.author_name}
              {receipt.charity_note.author_title ? `, ${receipt.charity_note.author_title}` : ''}
              {receipt.charity_note.charity_name ? ` · ${receipt.charity_note.charity_name}` : ''}
            </p>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <Building2 className="w-4 h-4 text-slate-500" />
            <p className="text-sm text-slate-400">Want this for your company?</p>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-sm font-semibold text-blue-400 hover:text-blue-300">
            See Altrunic <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <p className="mt-6 text-center text-xs text-slate-600 flex items-center justify-center gap-1.5">
          <Check className="w-3.5 h-3.5 text-blue-500" /> Verified 501(c)(3) recipient
        </p>
      </div>
    </div>
  );
};

export default DonationReceipt;
